/* eslint-disable @typescript-eslint/no-explicit-any */
import { PageTitle } from '@/components/atoms';
import { JSX, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import ACTIONPLANAPI from '@/apps/nnc/services/actionplan';
import ACTIONSAPI from '@/apps/nnc/services/actionplanactions';
import { useTranslation } from 'react-i18next';
import CircleIcon from '@mui/icons-material/Circle';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router';

export default function TasksPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [actionPlanTasks, setActionPlanTasks] = useState([]);
  const [actionsTasks, setActionsTasks] = useState([]);
  const [loadingActionPlanTasks, setLoadingActionPlanTasks] = useState(true);
  const [loadingActionsTasks, setLoadingActionsTasks] = useState(true);

  const getPendingActionPlans = async () => {
    try {
      setLoadingActionPlanTasks(true);
      const response = await ACTIONPLANAPI.tasks();
      setActionPlanTasks(response.data);
      setLoadingActionPlanTasks(false);
    } catch (error) {
      console.error('Error fetching pending action plans:', error);
      setLoadingActionPlanTasks(false);
      setActionPlanTasks([]);
    }
  };

  const getPendingActions = async () => {
    try {
      setLoadingActionsTasks(true);
      const response = await ACTIONSAPI.tasks();
      setActionsTasks(response.data);
      setLoadingActionsTasks(false);
    } catch (error) {
      setLoadingActionsTasks(false);
      console.error('Error fetching pending actions:', error);
      setActionsTasks([]);
    }
  };

  useEffect(() => {
    getPendingActionPlans();
    getPendingActions();
  }, []);

  const APcolumns = [
    {
      field: 'reference_number',
      headerName: t('Action Plan'),
      flex: 1,
    },
    {
      field: 'theme',
      headerName: t('Action Plan Theme'),
      flex: 2,
    },
    {
      field: 'actions_count',
      headerName: t('Actions Count'),
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={'flex'} sx={{ height: '100%' }} alignItems="center">
            <Box
              sx={{
                background: '#00d160',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 'bold',
                width: '80px',
                height: '30px',
              }}
            >
              {params?.row?.actions_count}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'created_at',
      headerName: t('Created At'),
      width: 150,
    },
    {
      field: 'finished_at',
      headerName: t('Finished At'),
      width: 150,
    },
    {
      field: 'duration',
      headerName: t('Duration'),
      width: 150,

      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box display={'flex'} sx={{ height: '100%' }} alignItems="center">
            <Box
              sx={{
                background: '#00d160',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                color: '#fff',
                fontWeight: 'bold',
                width: '80px',
                height: '30px',
              }}
            >
              {params?.row?.duration}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: t('Status'),
      width: 130,

      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            color={
              params?.row.status?.id == 1
                ? 'info'
                : params?.row.status?.id == 2
                  ? 'warning'
                  : params?.row.status?.id == 3
                    ? 'success'
                    : 'error'
            }
            icon={<CircleIcon sx={{ color: '#ffffff80 !important' }} />}
            label={params?.row.status?.label}
          />
        );
      },
    },
    {
      field: 'actionss',
      headerName: t('Actions'),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <GridActionsCellItem
            key={`edit-${params?.row.id}`}
            icon={<EditIcon />}
            onClick={() =>
              navigate(`/nnc/actionplan/add-update/${params?.row?.id}`)
            }
            label="edit"
          />
        );
      },
      width: 90,
    },
  ];

  const ActionsColumns = [
    {
      field: 'reference_number',
      headerName: t('Action'),
      flex: 1,
    },
    {
      field: 'origin',
      headerName: t('Origin'),
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const origins = params.row.action_plan?.failure_mode?.map(
          (i: any) => i.origin?.label,
        );
        return (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={1}
            sx={{ height: '100%' }}
          >
            {origins[0] || ''}
            {origins.length > 1 && (
              <Tooltip
                title={
                  <>
                    {origins.slice(1).map((machine: string, index: number) => (
                      <Box key={index} sx={{ marginBottom: '4px' }}>
                        {machine}
                      </Box>
                    ))}
                  </>
                }
              >
                <Box
                  sx={{
                    background: '#00d160',
                    padding: '0 5px',
                    borderRadius: '4px',
                    color: '#fff',
                    minWidth: '30px',
                    display: 'flex',
                    height: '30px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  +{origins.length - 1 || 0}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'description',
      headerName: t('Description'),
      flex: 2,
    },
    {
      field: 'responsible',
      headerName: t('Responsible'),
      flex: 1,
    },
    {
      field: 'due_date',
      headerName: t('Due Date'),
      flex: 1,
    },
    {
      field: 'status',
      headerName: t('Status'),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Chip
            color={
              params?.row.status?.id == 1
                ? 'info'
                : params?.row.status?.id == 2
                  ? 'warning'
                  : params?.row.status?.id == 3
                    ? 'success'
                    : 'error'
            }
            icon={<CircleIcon sx={{ color: '#ffffff80 !important' }} />}
            label={params?.row.status?.label}
          />
        );
      },
      flex: 1,
    },
    {
      field: 'actionss',
      headerName: t('Actions'),
      renderCell: (params: GridRenderCellParams) => {
        return (
          <GridActionsCellItem
            key={`edit-${params?.row.id}`}
            icon={<EditIcon />}
            onClick={() =>
              navigate(
                `/nnc/actionplan/add-update/${params?.row.action_plan?.id}`,
              )
            }
            label="edit"
          />
        );
      },
      width: 90,
    },
  ];

  return (
    <>
      <PageTitle title={t('My Tasks')} />
      <Box>
        <Stack spacing={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" fontWeight={600}>
                {t('Pending Action Plans')}
              </Typography>
            </Box>
            {loadingActionPlanTasks ? (
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ mr: 2, borderRadius: 1 }}
              />
            ) : (
              <DataGrid
                rows={actionPlanTasks}
                columns={APcolumns}
                hideFooter
                disableColumnMenu
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2,
                  '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f7fa' },
                }}
              />
            )}
          </Paper>

          <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" fontWeight={600}>
                {t('Pending Actions')}
              </Typography>
            </Box>
            {loadingActionsTasks ? (
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ mr: 2, borderRadius: 1 }}
              />
            ) : (
              <DataGrid
                rows={actionsTasks}
                columns={ActionsColumns}
                hideFooter
                disableColumnMenu
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 2,
                  '& .MuiDataGrid-columnHeaders': { bgcolor: '#f5f7fa' },
                }}
              />
            )}
          </Paper>
        </Stack>
      </Box>
    </>
  );
}
